import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, map, tap } from 'rxjs';

import { environment } from '../../../environments/environment';
import { ApiEnvelope, AuthService } from '../services/auth.service';

const ADMIN_API = `${environment.apiBaseUrl}/api/admin`;

/** Response body of POST /api/admin/impersonate/{employerId}. */
export interface ImpersonateData {
  accessToken:  string;
  /** ISO 8601 — when the impersonation token expires. */
  expiresAt:    string;
  employerName: string;
  employerId:   string;
}

/**
 * Admin "Concierge Mode" — read-only impersonation of an employer.
 *
 * `enter()` adopts the impersonation access token into the in-memory store (the
 * admin's own refresh token is untouched). `exit()` restores the admin's session
 * via /refresh and returns to the console. `active` reflects the ReadOnly JWT.
 */
@Injectable({ providedIn: 'root' })
export class ConciergeService {
  private readonly http   = inject(HttpClient);
  private readonly auth   = inject(AuthService);
  private readonly router = inject(Router);

  /** Impersonated employer's display name + token expiry (in-memory, ephemeral). */
  readonly employerName = signal<string | null>(null);
  readonly expiresAt    = signal<string | null>(null);

  /** True while a read-only impersonation session is active (ReadOnly JWT). */
  readonly active = computed(() => this.auth.isConcierge());

  enter(employerId: string): Observable<ImpersonateData> {
    return this.http
      .post<ApiEnvelope<ImpersonateData>>(`${ADMIN_API}/impersonate/${encodeURIComponent(employerId)}`, {})
      .pipe(
        map(env => {
          if (!env || env.success === false || !env.data) {
            throw new Error(env?.message || 'Could not enter concierge mode.');
          }
          return env.data;
        }),
        tap(data => {
          this.auth.applyImpersonationToken(data.accessToken, data.employerName);
          this.employerName.set(data.employerName);
          this.expiresAt.set(data.expiresAt);
        })
      );
  }

  /** Exit concierge: restore the admin's own session via /refresh, then return to
   *  the console. Always navigates back, even if the refresh call fails. */
  exit(): void {
    const done = (): void => {
      this.clear();
      void this.router.navigateByUrl('/admin/concierge');
    };
    this.auth.refreshToken().subscribe({ next: done, error: done });
  }

  private clear(): void {
    this.employerName.set(null);
    this.expiresAt.set(null);
  }
}
